import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { API } from '../App';
import { imgSrc } from '../lib/imgSrc';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { toast } from 'sonner';

const AddMachineryDialog = ({ open, onClose, plantLocationId, locations, onSuccess }) => {
  const [formData, setFormData] = useState({
    plant_location_id: plantLocationId || '',
    component: '',
    location: '',
    manufacturer: '',
    model: '',
    serial_number: '',
    status: 'operational',
    notes: ''
  });
  const [availableLocations, setAvailableLocations] = useState([]);
  const [imageFile, setImageFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);
  const hasInitialized = useRef(false);

  // Fetch locations when dialog opens
  useEffect(() => {
    if (open && !hasInitialized.current) {
      hasInitialized.current = true;
      
      // If locations were passed as prop, use them
      if (locations && locations.length > 0) {
        setAvailableLocations(locations);
      } else {
        // Otherwise fetch from API
        axios.get(`${API}/locations`).then(res => {
          setAvailableLocations(res.data);
        }).catch(() => {});
      }
      
      // Reset form
      setFormData({
        plant_location_id: plantLocationId || '',
        component: '',
        location: '',
        manufacturer: '',
        model: '',
        serial_number: '',
        status: 'operational',
        notes: ''
      });
      setImageFile(null);
      setImagePreview(null);
    }
    
    // Reset the initialization flag when dialog closes
    if (!open) {
      hasInitialized.current = false;
    }
  }, [open, plantLocationId, locations]);

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImageFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setUploading(true);

    try {
      let imageUrl = null;
      if (imageFile) {
        const formDataImage = new FormData();
        formDataImage.append('file', imageFile);
        const uploadRes = await axios.post(`${API}/upload-image`, formDataImage);
        imageUrl = uploadRes.data.image_url;
      }

      const submitData = {
        ...formData,
        plant_location_id: formData.plant_location_id || null,
        main_image: imageUrl,
      };

      await axios.post(`${API}/machinery`, submitData);
      toast.success('Equipment added successfully!');
      onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add equipment');
    } finally {
      setUploading(false);
    }
  };

  const handleClose = () => {
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Add New Equipment</DialogTitle>
          <DialogDescription>Enter details for new equipment</DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Plant Location Selector */}
          <div className="space-y-2">
            <Label htmlFor="plant_location">Plant Location</Label>
            <Select 
              value={formData.plant_location_id || "unassigned"} 
              onValueChange={(value) => setFormData({ ...formData, plant_location_id: value === "unassigned" ? "" : value })}
            >
              <SelectTrigger data-testid="plant-location-select">
                <SelectValue placeholder="Select location (or leave unassigned)" />
              </SelectTrigger>
              <SelectContent position="popper" sideOffset={4} className="z-[9999]">
                <SelectItem value="unassigned">Unassigned</SelectItem>
                {availableLocations.map((loc) => (
                  <SelectItem key={loc.id} value={loc.id}>{loc.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="component">Component *</Label>
              <Input
                id="component"
                data-testid="component-input"
                value={formData.component}
                onChange={(e) => setFormData({ ...formData, component: e.target.value })}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="location">Local Location *</Label>
              <Input
                id="location"
                data-testid="location-input"
                value={formData.location}
                onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                placeholder="e.g., Building A, Room 101"
                required
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="manufacturer">Manufacturer *</Label>
              <Input
                id="manufacturer"
                data-testid="manufacturer-input"
                value={formData.manufacturer}
                onChange={(e) => setFormData({ ...formData, manufacturer: e.target.value })}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="model">Model *</Label>
              <Input
                id="model"
                data-testid="model-input"
                value={formData.model}
                onChange={(e) => setFormData({ ...formData, model: e.target.value })}
                required
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="serial_number">Serial Number *</Label>
            <Input
              id="serial_number"
              data-testid="serial-number-input"
              value={formData.serial_number}
              onChange={(e) => setFormData({ ...formData, serial_number: e.target.value })}
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="status">Status</Label>
            <Select value={formData.status} onValueChange={(value) => setFormData({ ...formData, status: value })}>
              <SelectTrigger data-testid="status-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent position="popper" sideOffset={4} className="z-[9999]">
                <SelectItem value="operational">Operational</SelectItem>
                <SelectItem value="maintenance_needed">Maintenance Needed</SelectItem>
                <SelectItem value="down">Down</SelectItem>
                <SelectItem value="lockout">Lockout</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="image">Main Image</Label>
            <Input
              id="image"
              data-testid="image-input"
              type="file"
              accept="image/*"
              onChange={handleImageChange}
            />
            {imagePreview && (
              <div className="mt-2">
                <img src={imgSrc(imagePreview)} alt="Preview" className="max-h-40 rounded" />
              </div>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="notes">Notes</Label>
            <Textarea
              id="notes"
              data-testid="notes-textarea"
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              rows={3}
            />
          </div>

          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={handleClose} disabled={uploading}>
              Cancel
            </Button>
            <Button type="submit" disabled={uploading} data-testid="submit-machinery-btn">
              {uploading ? 'Adding...' : 'Add Equipment'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default AddMachineryDialog;
